import { useState, type FormEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIvrs, useSaveIvr, useDeleteTms } from '../../lib/api/hooks-tms.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import { api, ApiClientError } from '../../lib/api/client.js';
import type { IvrMenu, Destination } from '@arcnet/contracts';

interface KeyDraft { label: string; destination: Destination | null }
type KeyMap = Record<string, KeyDraft>;

/**
 * The twelve keys a caller actually has, in handset order.
 *
 * Modelling the options as a map keyed by digit rather than as a list of rows
 * makes a duplicate key unrepresentable: the old editor let two rows claim
 * "1" and only warned about it. Here a second assignment simply replaces the
 * first, which is what the switch would have done anyway.
 */
const KEYS: ReadonlyArray<{ digit: string; sub: string }> = [
  { digit: '1', sub: '' },     { digit: '2', sub: 'abc' },  { digit: '3', sub: 'def' },
  { digit: '4', sub: 'ghi' },  { digit: '5', sub: 'jkl' },  { digit: '6', sub: 'mno' },
  { digit: '7', sub: 'pqrs' }, { digit: '8', sub: 'tuv' },  { digit: '9', sub: 'wxyz' },
  { digit: '*', sub: '' },     { digit: '0', sub: 'oper' }, { digit: '#', sub: '' },
];

function Dialpad({ tenantId, keys, selected, onSelect }: {
  tenantId: string; keys: KeyMap; selected: string; onSelect: (d: string) => void;
}) {
  return (
    <div className="arc-dialpad" role="group" aria-label="IVR keys">
      {KEYS.map(({ digit, sub }) => {
        const opt = keys[digit];
        const assigned = Boolean(opt?.destination);
        return (
          <button
            key={digit} type="button" className="arc-key"
            data-assigned={assigned} data-selected={selected === digit}
            aria-pressed={selected === digit}
            aria-label={`Key ${digit}${assigned ? ', assigned' : ', not assigned'}`}
            title={opt?.label || undefined}
            onClick={() => onSelect(digit)}
          >
            {assigned && <span className="arc-key-dot" />}
            <span className="arc-key-digit">{digit}</span>
            {assigned ? (
              <span className="arc-key-label">
                {opt?.label
                  ? opt.label
                  : <DestinationLabel tenantId={tenantId} destination={opt?.destination ?? null} />}
              </span>
            ) : (
              <span className="arc-key-sub">{sub}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function IvrsTab({ tenantId }: { tenantId: string }) {
  const ivrs = useIvrs(tenantId);
  const del = useDeleteTms(tenantId, 'ivrs');
  const [editing, setEditing] = useState<IvrMenu | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">IVR menus</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            "Press 1 for sales" — a greeting, a set of keys, and what happens when nobody chooses.
          </p>
        </div>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'New IVR'}
        </button>
      </div>

      {ivrs.error && <ErrorBanner error={ivrs.error} />}
      {del.error && <ErrorBanner error={del.error} />}
      {(creating || editing) && (
        <IvrEditor key={editing?.id ?? 'new'} tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }} />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Dial</th><th>Options</th>
                <th>Waits</th><th>Nothing pressed</th><th>Wrong key</th><th />
              </tr>
            </thead>
            {ivrs.isLoading ? <TableSkeleton cols={7} /> : (
              <tbody>
                {ivrs.data?.data.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontWeight: 550 }}>{m.name}</td>
                    <td className="arc-mono">{m.extension ?? <span className="arc-subtle">—</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {m.options.length === 0
                          ? <span className="arc-subtle">none</span>
                          : m.options.map((o) => (
                              <Badge key={o.id} tone="muted">
                                <span className="arc-mono" style={{ fontSize: 11 }}>{o.digit}</span>
                                {o.label ? ` ${o.label}` : ''}
                              </Badge>
                            ))}
                        {m.direct_dial && <Badge tone="info">direct dial</Badge>}
                      </div>
                    </td>
                    <td className="arc-muted">
                      {(m.answer_after_rings ?? 0) > 0 && `${m.answer_after_rings} rings · `}
                      {m.timeout_sec}s × {m.max_attempts}
                    </td>
                    <td><DestinationLabel tenantId={tenantId} destination={m.timeout_destination} /></td>
                    <td>
                      {m.invalid_destination
                        ? <DestinationLabel tenantId={tenantId} destination={m.invalid_destination} />
                        : <span className="arc-subtle">as nothing pressed</span>}
                    </td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { setEditing(m); setCreating(false); }}>Edit</button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { if (confirm(`Delete IVR '${m.name}'?`)) del.mutate(m.id); }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!ivrs.isLoading && ivrs.data?.data.length === 0 && (
          <Empty icon="ivr" title="No IVR menus"
            hint="An IVR answers, plays a greeting and routes on the key pressed." />
        )}
      </div>
    </>
  );
}

/** One part of the menu: a heading, what it controls, and the controls. */
function Part({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <section className="arc-method">
      <h3 className="arc-method-title">{title}</h3>
      <p className="arc-method-hint">{hint}</p>
      {children}
    </section>
  );
}

function IvrEditor({ tenantId, existing, onDone }: {
  tenantId: string; existing: IvrMenu | null; onDone: () => void;
}) {
  const save = useSaveIvr(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [code, setCode] = useState(existing?.extension ?? '');
  const [greetingId, setGreetingId] = useState<string>(existing?.greeting_media_id ?? '');
  const [retryId, setRetryId] = useState<string>(existing?.invalid_media_id ?? '');
  const [timeoutSec, setTimeoutSec] = useState(String(existing?.timeout_sec ?? 5));
  const [digitSec, setDigitSec] = useState(String(existing?.digit_timeout_sec ?? 3));
  const [tries, setTries] = useState(String(existing?.max_attempts ?? 3));
  const [rings, setRings] = useState(String(existing?.answer_after_rings ?? 0));
  const [directDial, setDirectDial] = useState(existing?.direct_dial ?? false);
  const [onTimeout, setOnTimeout] = useState<Destination | null>(existing?.timeout_destination ?? null);
  const [onInvalid, setOnInvalid] = useState<Destination | null>(existing?.invalid_destination ?? null);

  /**
   * Greetings and announcements both, because the distinction is about where a
   * file is normally used rather than what a menu is allowed to play.
   */
  const media = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{ data: Array<{ id: string; kind: string; name: string; duration_sec: number | null }> }>(
      `/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });
  const files = media.data?.data ?? [];
  const greetings = files.filter((m) => m.kind === 'greeting' || m.kind === 'announcement');
  const prompts = files.filter((m) => m.kind === 'prompt' || m.kind === 'announcement' || m.kind === 'greeting');
  const length = (m: { duration_sec: number | null }) => (m.duration_sec ? ` (${m.duration_sec.toFixed(1)}s)` : '');

  const [keys, setKeys] = useState<KeyMap>(() => {
    const init: KeyMap = {};
    for (const o of existing?.options ?? []) {
      init[o.digit] = { label: o.label ?? '', destination: o.destination };
    }
    return init;
  });
  const [selected, setSelected] = useState('1');

  const setKey = (digit: string, patch: Partial<KeyDraft>) =>
    setKeys((prev) => ({
      ...prev,
      [digit]: { label: '', destination: null, ...prev[digit], ...patch },
    }));

  const current = keys[selected];
  const assignedKeys = KEYS.filter((k) => keys[k.digit]?.destination);
  // With direct dial on, a key bound to an option is consumed as that option,
  // so extensions starting with that digit become unreachable from the menu.
  const shadowed = directDial
    ? assignedKeys.map((k) => k.digit).filter((d) => d >= '0' && d <= '9')
    : [];
  const err = save.error instanceof ApiClientError ? save.error : null;
  const playsTimes = Number(tries) || 1;

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name, extension: code || null,
          greeting_media_id: greetingId || null,
          invalid_media_id: retryId || null,
          timeout_sec: Number(timeoutSec),
          digit_timeout_sec: Number(digitSec),
          max_attempts: Number(tries),
          answer_after_rings: Math.min(10, Math.max(0, Math.floor(Number(rings) || 0))),
          direct_dial: directDial,
          timeout_destination: onTimeout,
          invalid_destination: onInvalid,
          options: KEYS.flatMap(({ digit }) => {
            const k = keys[digit];
            if (!k?.destination) return [];
            return [{
              digit,
              ...(k.label ? { label: k.label } : {}),
              destination: k.destination,
            }];
          }),
        }, { onSuccess: onDone });
      }}
    >
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.name}` : 'New IVR menu'}
      </h3>
      {err && <ErrorBanner error={err} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1.4fr) minmax(110px, 0.5fr) minmax(220px, 1.6fr)', gap: 12 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iv-name">Name</label>
          <input id="iv-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Main menu" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iv-code">Dial code</label>
          <input id="iv-code" className="arc-input arc-mono" inputMode="numeric" maxLength={6}
            value={code} placeholder="none"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iv-greeting">Greeting</label>
          <select id="iv-greeting" className="arc-input" value={greetingId}
            onChange={(e) => setGreetingId(e.target.value)}>
            <option value="">None — wait in silence</option>
            {greetings.map((m) => <option key={m.id} value={m.id}>{m.name}{length(m)}</option>)}
          </select>
          {/* Interruptible, and worth saying: it changes how the recording is
              written. "Press 1 for sales" belongs at the start, not after a
              welcome nobody who calls twice will listen to. */}
          <p className="arc-hint">
            {greetings.length === 0
              ? 'Nothing uploaded yet: add one under Media, then choose it here.'
              : 'A caller can press a key while it plays.'}
          </p>
        </div>
      </div>

      <Part title="Keys" hint="Where each key sends the caller. Keys with nothing on them count as a wrong key.">
        <div className="arc-row" style={{ marginBottom: 6 }}>
          <div className="arc-spacer" />
          <span className="arc-muted" style={{ fontSize: 11.5 }}>
            {assignedKeys.length} of {KEYS.length} keys assigned
          </span>
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Dialpad tenantId={tenantId} keys={keys} selected={selected} onSelect={setSelected} />

          <div className="arc-key-panel">
            <div className="arc-key-panel-head">
              <span className="arc-key-chip">{selected}</span>
              <div className="arc-stack">
                <strong style={{ fontSize: 12.5 }}>Key {selected}</strong>
                <span className="arc-muted" style={{ fontSize: 11.5 }}>
                  Where the caller goes when they press it.
                </span>
              </div>
              <div className="arc-spacer" />
              {current?.destination && (
                <button
                  type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => setKeys((prev) => {
                    const next = { ...prev };
                    delete next[selected];
                    return next;
                  })}
                >
                  <Icon name="trash" size={12} /> Clear
                </button>
              )}
            </div>

            <div className="arc-field">
              <label className="arc-label">Send to</label>
              <DestinationPicker
                tenantId={tenantId} value={current?.destination ?? null}
                onChange={(d) => setKey(selected, { destination: d })} allowNone
              />
            </div>

            <div className="arc-field" style={{ marginBottom: 0 }}>
              <label className="arc-label" htmlFor="iv-klabel">Label</label>
              <input
                id="iv-klabel" className="arc-input" placeholder="Sales"
                value={current?.label ?? ''}
                onChange={(e) => setKey(selected, { label: e.target.value })}
              />
              <p className="arc-muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                Shown on the keypad and in reports. Callers never hear it.
              </p>
            </div>
          </div>
        </div>

        <div className="arc-row" style={{ marginTop: 12, gap: 10, alignItems: 'flex-start' }}>
          <Switch id="iv-dd" checked={directDial} onChange={setDirectDial} />
          <label htmlFor="iv-dd" style={{ cursor: 'pointer' }}>
            Callers may dial an extension directly
            <span className="arc-muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
              Internal numbers only, two to six digits. Nobody can reach an outside line from the menu.
            </span>
          </label>
        </div>

        {shadowed.length > 0 && (
          <div className="arc-banner arc-banner-info" style={{ marginTop: 10 }}>
            <Icon name="info" size={14} />
            <span>
              Extensions starting with <span className="arc-mono">{shadowed.join(', ')}</span> cannot
              be dialled from this menu: those keys are taken by an option.
            </span>
          </div>
        )}
      </Part>

      <Part title="Timing and retries"
        hint={`The menu plays up to ${playsTimes} ${playsTimes === 1 ? 'time' : 'times'}. A wrong key and silence each use one, and the retry prompt plays before it repeats.`}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
          {/* First, because it is the first thing a caller meets: ringing, and
              only then the greeting (owner, 14 September 2026). */}
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="iv-rings">Rings before answering</label>
            <input id="iv-rings" className="arc-input" type="number" min={0} max={10}
              value={rings} onChange={(e) => setRings(e.target.value)} />
            <p className="arc-hint">
              {Number(rings) > 0
                ? `About ${Math.min(10, Math.floor(Number(rings))) * 3} seconds of ringing, then the greeting.`
                : 'Answers straight away.'}
            </p>
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="iv-to">Wait for a key (s)</label>
            <input id="iv-to" className="arc-input" type="number" min={1} max={60}
              value={timeoutSec} onChange={(e) => setTimeoutSec(e.target.value)} />
            <p className="arc-hint">After the greeting finishes.</p>
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="iv-tries">Plays</label>
            <input id="iv-tries" className="arc-input" type="number" min={1} max={10}
              value={tries} onChange={(e) => setTries(e.target.value)} />
            <p className="arc-hint">Before the caller is sent on.</p>
          </div>
          {directDial && (
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="iv-digit">Gap between digits (s)</label>
              <input id="iv-digit" className="arc-input" type="number" min={1} max={10}
                value={digitSec} onChange={(e) => setDigitSec(e.target.value)} />
              {/* The cost of direct dialling, where it is switched on: a
                  one-key choice cannot be taken until the switch is sure no
                  second digit is coming. */}
              <p className="arc-hint">A one-key choice also waits this long, in case more digits follow.</p>
            </div>
          )}
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="iv-retry">Retry prompt</label>
            <select id="iv-retry" className="arc-input" value={retryId}
              onChange={(e) => setRetryId(e.target.value)}>
              <option value="">None — a short pause</option>
              {prompts.map((m) => <option key={m.id} value={m.id}>{m.name}{length(m)}</option>)}
            </select>
          </div>
        </div>
      </Part>

      <Part title="When the caller does not choose"
        hint="Where the call goes once the plays are used up. Somebody who pressed a wrong key goes to the second, if one is set.">
        <div className="arc-grid arc-grid-2">
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label">Nothing pressed</label>
            <DestinationPicker tenantId={tenantId} value={onTimeout} onChange={setOnTimeout} allowNone />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label">A wrong key pressed</label>
            <DestinationPicker tenantId={tenantId} value={onInvalid} onChange={setOnInvalid} allowNone />
            {!onInvalid && <p className="arc-hint">Not set: the same as nothing pressed.</p>}
          </div>
        </div>
      </Part>

      <div className="arc-row" style={{ gap: 8, marginTop: 18 }}>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create IVR'}
        </button>
        <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}
